import React, { useCallback, useEffect, useState } from "react";
import Header from "../../../components/Header/Header";
import Tabs from "../../../components/Tabs/Tabs";
import mes_tab_data from "../../../assets/mockedData/mes_tab_data";
import { savePathToLocalStorage } from "../../../utils/helpers";
import mesApi from "../../../Api/mes.api";
import {
  Timeline,
  TimelineBatch,
  Recipe,
  Machine,
  STARTER_SETTING_KEYS,
} from "../../../types/mes";
import { ToastContainer, toast } from "react-toastify";
import PrimaryButton from "../../../components/Buttons/PrimaryButton";
import OutlinedButton from "../../../components/Buttons/OutlinedButton";
import MesDateSelector, { getDefaultMesDate, saveMesDate } from "../../../components/MES/MesDateSelector";
import { getRecipeLineName } from "../../../utils/recipeBatchLines";
import {
  DEFAULT_STARTER_PARAMS,
  StarterParams,
  StarterStupanj,
  izracunajBigu,
  izracunajPlanStartera,
  starterMixerIdFromSettings,
  starterParamsFromSettings,
  starterParamsToSettings,
} from "./starterCalc";

/**
 * Priprema startera i bige za jedan proizvodni dan.
 *
 * Količine startera dolaze iz potvrđenog plana (masa zamjesa × pekarski postotak
 * linija recepture), kapacitet zamjesa iz registra strojeva, a omjeri i
 * hidratacije iz MES postavki — stranica sama ne drži nijednu tehnološku
 * konstantu. Sam izračun je u ./starterCalc i ide unatrag: od mase koju dan
 * traži prema osvježavanju koje je daje.
 */

type StarterType = "starter" | "razeni" | "biga";

interface DemandDetail {
  id_zamjesa: string;
  product_name: string;
  finalna_masa_kg: number;
  tip: StarterType;
  potrebno_kg: number;
}

/** Parametri u obliku u kojem se uređuju; hidratacije su u postocima. */
type ParamForm = Record<keyof Omit<StarterParams, "kapacitetMikseraKg">, string>;

const HYDRATION_FIELDS = [
  "hidratacijaNocni",
  "hidratacijaJutro",
  "hidratacijaRazeni",
  "hidratacijaDnevni",
] as const;

const isHydration = (field: keyof ParamForm) =>
  (HYDRATION_FIELDS as readonly string[]).includes(field);

function paramsToForm(params: StarterParams): ParamForm {
  const form = {} as ParamForm;
  (Object.keys(params) as Array<keyof StarterParams>).forEach((field) => {
    if (field === "kapacitetMikseraKg") return;
    const value = params[field];
    form[field] = String(isHydration(field) ? value * 100 : value);
  });
  return form;
}

function formToParams(form: ParamForm, kapacitetMikseraKg: number): StarterParams {
  const params = { ...DEFAULT_STARTER_PARAMS, kapacitetMikseraKg };
  (Object.keys(form) as Array<keyof ParamForm>).forEach((field) => {
    const n = parseFloat(form[field]);
    if (!Number.isFinite(n) || n < 0) return;
    params[field] = isHydration(field) ? n / 100 : n;
  });
  return params;
}

/** Klasificiraj liniju recepture u starter / raženi / biga (po nazivu). */
function classifyStarterLine(name: string): StarterType | null {
  const n = name.toUpperCase();
  if (n.includes("RAŽEN") || n.includes("RAZEN")) return "razeni";
  if (n.includes("BIGA")) return "biga";
  if (n.includes("STARTER")) return "starter";
  return null;
}

const round2 = (n: number) => Math.round((Number.isFinite(n) ? n : 0) * 100) / 100;

const StarterPlan = () => {
  savePathToLocalStorage();

  const [selectedDate, setSelectedDate] = useState<string>(getDefaultMesDate(true));
  const [timeline, setTimeline] = useState<Timeline | null>(null);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [savingParams, setSavingParams] = useState(false);

  // Izračunata potražnja iz plana (prije ručnog override-a)
  const [demand, setDemand] = useState<{ starter: number; razeni: number; biga: number }>({
    starter: 0,
    razeni: 0,
    biga: 0,
  });
  const [details, setDetails] = useState<DemandDetail[]>([]);

  // Ručni override ukupne potrebe (iz plana, ali tehnolog može korigirati)
  const [override, setOverride] = useState<{ starter: string; razeni: string; biga: string }>({
    starter: "",
    razeni: "",
    biga: "",
  });

  const [paramForm, setParamForm] = useState<ParamForm>(paramsToForm(DEFAULT_STARTER_PARAMS));
  const [showParams, setShowParams] = useState(false);
  const [mixers, setMixers] = useState<Machine[]>([]);
  const [mixerId, setMixerId] = useState<number | null>(null);

  const handleDateChange = (date: string) => {
    saveMesDate(date);
    setSelectedDate(date);
  };

  /**
   * Potražnja iz recepture: za svaki finalni zamjes zbroji
   * masa × pekarski % linija imenovanih STARTER / RAŽENI STARTER / BIGA.
   */
  const calculateNeeds = useCallback(async (tl: Timeline) => {
    setCalculating(true);
    try {
      const batches: TimelineBatch[] = (tl.TimelineBatch || []).filter(
        (b) => !b.is_semi_finished
      );

      const recipeIds = Array.from(
        new Set(batches.map((b) => b.RecipeID).filter((id): id is number => !!id))
      );

      const recipeMap = new Map<number, Recipe>();
      await Promise.all(
        recipeIds.map(async (id) => {
          try {
            recipeMap.set(id, await mesApi.getRecipe(id));
          } catch {
            /* recept koji se ne može dohvatiti preskačemo */
          }
        })
      );

      let starter = 0;
      let razeni = 0;
      let biga = 0;
      const detail: DemandDetail[] = [];

      for (const b of batches) {
        const mass = Number(b.FinalMassKg) || 0;
        if (!mass || !b.RecipeID) continue;
        const recipe = recipeMap.get(b.RecipeID);
        const lines = recipe?.RecipeIngredient_RecipeToRecipeIngredient_Recipe || [];
        // Scale by TOTAL dough mass, not by an assumed 100% column. Baker % sum to
        // >100 (flour=100 + hydration + salt + …), so kg = pct * (mass / Σ all %).
        // Dividing by 100 over-counted every starter/biga quantity by ~Σ%/100.
        const sumPct = lines.reduce((s, l) => s + (Number(l.BakerPercentage) || 0), 0);
        const factor = sumPct > 0 ? mass / sumPct : 0;
        for (const line of lines) {
          const name = getRecipeLineName(line);
          const tip = classifyStarterLine(name);
          if (!tip) continue;
          const pct = Number(line.BakerPercentage) || 0;
          if (pct === 0) continue;
          const kg = pct * factor;
          if (tip === "razeni") razeni += kg;
          else if (tip === "biga") biga += kg;
          else starter += kg;
          detail.push({
            id_zamjesa: b.MixCode,
            product_name: b.ProductName || "",
            finalna_masa_kg: mass,
            tip,
            potrebno_kg: kg,
          });
        }
      }

      starter = round2(starter);
      razeni = round2(razeni);
      biga = round2(biga);

      setDemand({ starter, razeni, biga });
      setDetails(detail);
      setOverride({
        starter: starter ? String(starter) : "",
        razeni: razeni ? String(razeni) : "",
        biga: biga ? String(biga) : "",
      });

      if (starter === 0 && razeni === 0 && biga === 0) {
        toast.info("Plan ne sadrži startere ni bigu za odabrani datum.");
      }
    } catch (err: any) {
      toast.error("Nije moguće izračunati potrebe za starterom: " + (err?.message || ""));
      setDemand({ starter: 0, razeni: 0, biga: 0 });
      setDetails([]);
      setOverride({ starter: "", razeni: "", biga: "" });
    } finally {
      setCalculating(false);
    }
  }, []);

  const loadTimelineForDate = useCallback(
    async (date: string) => {
      setLoading(true);
      try {
        const data = await mesApi.getTimelineByDate(date);
        setTimeline(data);
        if (data && data.Status === "confirmed") {
          await calculateNeeds(data);
        } else {
          setDemand({ starter: 0, razeni: 0, biga: 0 });
          setDetails([]);
          setOverride({ starter: "", razeni: "", biga: "" });
        }
      } catch {
        setTimeline(null);
        setDemand({ starter: 0, razeni: 0, biga: 0 });
        setDetails([]);
        setOverride({ starter: "", razeni: "", biga: "" });
      } finally {
        setLoading(false);
      }
    },
    [calculateNeeds]
  );

  useEffect(() => {
    loadTimelineForDate(selectedDate);
  }, [selectedDate, loadTimelineForDate]);

  // Parametri i mikser su vezani uz pogon, ne uz datum — čitaju se jednom.
  useEffect(() => {
    const loadSetup = async () => {
      const [settings, machines] = await Promise.all([
        mesApi.getMesSettings().catch(() => []),
        mesApi.getMachines({ machine_type: "Mikser" }).catch(() => []),
      ]);
      const active = machines.filter((m) => m.IsActive !== false);
      setMixers(active);
      setParamForm(paramsToForm(starterParamsFromSettings(settings)));

      const saved = starterMixerIdFromSettings(settings);
      // Dok mikser nije izabran, uzmi najmanji koji ima upisan kapacitet:
      // noćni starter je najmanji zamjes dana i ide na najmanji stroj.
      const fallback = active
        .filter((m) => Number(m.CapacityMaxKg) > 0)
        .sort((a, b) => Number(a.CapacityMaxKg) - Number(b.CapacityMaxKg))[0];
      setMixerId(
        saved && active.some((m) => m.ID === saved) ? saved : fallback ? fallback.ID : null
      );
    };
    loadSetup();
  }, []);

  const selectedMixer = mixers.find((m) => m.ID === mixerId) || null;
  const mixerCapacity = Number(selectedMixer?.CapacityMaxKg) || 0;
  const params = formToParams(
    paramForm,
    mixerCapacity > 0 ? mixerCapacity : DEFAULT_STARTER_PARAMS.kapacitetMikseraKg
  );

  const handleParamChange = (field: keyof ParamForm, value: string) =>
    setParamForm((prev) => ({ ...prev, [field]: value }));

  const handleSaveParams = async () => {
    setSavingParams(true);
    try {
      await mesApi.updateMesSettings({
        ...starterParamsToSettings(params),
        [STARTER_SETTING_KEYS.MIXER_MACHINE_ID]: mixerId ? String(mixerId) : "",
      });
      toast.success("Parametri startera spremljeni.");
    } catch (err: any) {
      toast.error("Spremanje parametara nije uspjelo: " + (err?.message || ""));
    } finally {
      setSavingParams(false);
    }
  };

  const handleResetParams = () => setParamForm(paramsToForm(DEFAULT_STARTER_PARAMS));

  const ov = (v: string, fallback: number) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : fallback;
  };

  const starterUkupno = ov(override.starter, demand.starter);
  const razeniUkupno = ov(override.razeni, demand.razeni);
  const bigaUkupno = ov(override.biga, demand.biga);

  const plan = izracunajPlanStartera(starterUkupno, razeniUkupno, params);
  const bg = izracunajBigu(bigaUkupno);

  const hasAny = starterUkupno > 0 || razeniUkupno > 0 || bigaUkupno > 0;

  const fmt = (n: number, d = 2) => (Number.isFinite(n) ? n : 0).toFixed(d);
  const pct = (n: number) => `${(n * 100).toFixed(0)}%`;

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      toast.error("Nije moguće otvoriti prozor za ispis");
      return;
    }
    const datumFmt = new Date(selectedDate).toLocaleDateString("hr-HR");
    const zamLabel = (n: number) => `${n} zamjes${n !== 1 ? "a" : ""}`;

    const nocniHtml =
      plan.nocni.ukupno > 0
        ? `
      <h2>1. NOĆNI STARTER &nbsp; — &nbsp; ${fmt(plan.nocni.ukupno)} kg &nbsp;·&nbsp; ${zamLabel(plan.nocni.nZamjesa)}</h2>
      <p class="hint">Priprema dan prije, ~12h | omjer 1 (matica) : ${fmt(params.omjerBrasnoNocni, 2)} (brašno) uz ${pct(params.hidratacijaNocni)} hidratacije
      | mikser ${selectedMixer ? selectedMixer.MachineName : "—"} (${fmt(params.kapacitetMikseraKg, 0)} kg/zamjes)</p>
      <table>
        <thead><tr><th>#</th><th>Sirovina</th><th>Ukupno [kg]</th><th>Po zamjesu [kg]</th></tr></thead>
        <tbody>
          <tr><td>1</td><td>STARTER DNEVNI/MATICA</td><td>${fmt(plan.nocni.starter)}</td><td>${fmt(plan.nocni.poZamjesu.starter)}</td></tr>
          <tr><td>2</td><td>MANITOBA BRAŠNO</td><td>${fmt(plan.nocni.brasno)}</td><td>${fmt(plan.nocni.poZamjesu.brasno)}</td></tr>
          <tr><td>3</td><td>VODA</td><td>${fmt(plan.nocni.voda)}</td><td>${fmt(plan.nocni.poZamjesu.voda)}</td></tr>
          <tr class="total"><td colspan="2">UKUPNO NOĆNI</td><td>${fmt(plan.nocni.ukupno)}</td><td>${fmt(plan.nocni.poZamjesu.ukupno)}</td></tr>
        </tbody>
      </table>
      <p class="hint">Od toga za jutarnji starter ${fmt(plan.jutro.starter)} kg${
        plan.razeni.ukupno > 0 ? `, za raženi starter ${fmt(plan.razeni.starter)} kg` : ""
      }.</p>`
        : "";

    const jutroHtml =
      plan.jutro.ukupno > 0
        ? `
      <h2>2. JUTARNJI STARTER &nbsp; — &nbsp; ${fmt(plan.jutro.ukupno)} kg</h2>
      <p class="hint">Radni nalog ${fmt(starterUkupno)} kg + sigurnosna ${fmt(params.sigurnosnaKg)} kg
      | omjer 1 (noćni) : ${fmt(params.omjerBrasnoJutro, 2)} (brašno) uz ${pct(params.hidratacijaJutro)} hidratacije</p>
      <table>
        <thead><tr><th>#</th><th>Sirovina</th><th>Zamjes [kg]</th></tr></thead>
        <tbody>
          <tr><td>1</td><td>STARTER NOĆNI</td><td>${fmt(plan.jutro.starter)}</td></tr>
          <tr><td>2</td><td>MANITOBA BRAŠNO</td><td>${fmt(plan.jutro.brasno)}</td></tr>
          <tr><td>3</td><td>VODA</td><td>${fmt(plan.jutro.voda)}</td></tr>
          <tr class="total"><td colspan="2">UKUPNO JUTARNJI</td><td>${fmt(plan.jutro.ukupno)}</td></tr>
        </tbody>
      </table>`
        : "";

    const razeniHtml =
      plan.razeni.ukupno > 0
        ? `
      <h2>3. RAŽENI STARTER &nbsp; — &nbsp; ${fmt(plan.razeni.ukupno)} kg</h2>
      <p class="hint">Omjer 1 (noćni) : ${fmt(params.omjerBrasnoRazeni, 2)} (raženo brašno) uz ${pct(params.hidratacijaRazeni)} hidratacije</p>
      <table>
        <thead><tr><th>#</th><th>Sirovina</th><th>Zamjes [kg]</th></tr></thead>
        <tbody>
          <tr><td>1</td><td>STARTER NOĆNI</td><td>${fmt(plan.razeni.starter)}</td></tr>
          <tr><td>2</td><td>RAŽENO BRAŠNO</td><td>${fmt(plan.razeni.brasno)}</td></tr>
          <tr><td>3</td><td>VODA</td><td>${fmt(plan.razeni.voda)}</td></tr>
          <tr class="total"><td colspan="2">UKUPNO RAŽENI</td><td>${fmt(plan.razeni.ukupno)}</td></tr>
        </tbody>
      </table>`
        : "";

    const dnevniHtml =
      plan.dnevni.ukupno > 0
        ? `
      <h2>4. DNEVNI STARTER / MATICA &nbsp; — &nbsp; ${fmt(plan.dnevni.ukupno)} kg</h2>
      <p class="hint">Ostaje za idući dan | omjer 1 (jutarnji) : ${fmt(params.omjerBrasnoDnevni, 2)} (brašno) uz ${pct(params.hidratacijaDnevni)} hidratacije</p>
      <table>
        <thead><tr><th>#</th><th>Sirovina</th><th>Zamjes [kg]</th></tr></thead>
        <tbody>
          <tr><td>1</td><td>STARTER JUTRO</td><td>${fmt(plan.dnevni.starter)}</td></tr>
          <tr><td>2</td><td>MANITOBA BRAŠNO</td><td>${fmt(plan.dnevni.brasno)}</td></tr>
          <tr><td>3</td><td>VODA</td><td>${fmt(plan.dnevni.voda)}</td></tr>
          <tr class="total"><td colspan="2">UKUPNO DNEVNI/MATICA</td><td>${fmt(plan.dnevni.ukupno)}</td></tr>
        </tbody>
      </table>`
        : "";

    const bigaHtml =
      bigaUkupno > 0
        ? `
      <h2>5. BIGA PRIPREMA &nbsp; — &nbsp; ${fmt(bigaUkupno)} kg</h2>
      <p class="hint">Priprema dan prije | smrznuti kvasac 0.05%, voda 38.63%</p>
      <table style="max-width:420px">
        <thead><tr><th>#</th><th>Sirovina</th><th>Za 1 kg</th><th>Zamjes [kg]</th></tr></thead>
        <tbody>
          <tr><td>1</td><td>MANITOBA BRAŠNO</td><td>0.6132</td><td>${fmt(bg.brasno)}</td></tr>
          <tr><td>2</td><td>SMRZNUTI KVASAC</td><td>0.0005</td><td>${fmt(bg.kvasac, 4)}</td></tr>
          <tr><td>3</td><td>VODA</td><td>0.3863</td><td>${fmt(bg.voda)}</td></tr>
          <tr class="total"><td colspan="3">UKUPNO BIGA</td><td>${fmt(bigaUkupno)}</td></tr>
        </tbody>
      </table>`
        : "";

    const html = `<!DOCTYPE html><html lang="hr"><head><meta charset="UTF-8">
<title>Priprema startera i bige - ${datumFmt}</title>
<style>
  body { font-family: Arial, Helvetica, sans-serif; font-size: 12px; color: #000; margin: 20px; }
  h1 { font-size: 18px; margin: 0; }
  h2 { font-size: 14px; margin: 16px 0 6px 0; background: #333; color: #fff; padding: 5px 10px; }
  .header { display: flex; justify-content: space-between; border-bottom: 3px solid #000; padding-bottom: 8px; margin-bottom: 12px; }
  .meta { text-align: right; font-size: 13px; }
  .hint { font-size: 10px; color: #444; margin: 2px 0 6px 0; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 10px; font-size: 11px; }
  th { background: #f0f0f0; border: 1px solid #999; padding: 4px 8px; text-align: left; }
  td { border: 1px solid #999; padding: 4px 8px; }
  tr:nth-child(even) { background: #fafafa; }
  .total { font-weight: bold; background: #e0e0e0 !important; }
  .signature { margin-top: 30px; display: flex; justify-content: space-between; }
  .signature div { border-top: 1px solid #000; width: 30%; text-align: center; padding-top: 4px; font-size: 11px; }
  .footer { margin-top: 16px; border-top: 1px solid #ccc; font-size: 10px; color: #666; padding-top: 6px; }
  @media print { @page { size: A4; margin: 15mm; } }
</style></head><body>
  <div class="header">
    <h1>PRIPREMA STARTERA I BIGE</h1>
    <div class="meta">Datum: <strong>${datumFmt}</strong></div>
  </div>
  ${nocniHtml}
  ${jutroHtml}
  ${razeniHtml}
  ${dnevniHtml}
  ${bigaHtml}
  <div class="signature">
    <div>Izdao (tehnolog)</div>
    <div>Odgovorni za starter</div>
    <div>Kontrola</div>
  </div>
  <div class="footer">Generirano iz MES sustava. Sva odstupanja od naloga upisati u napomenu.</div>
  <script>window.onload = function() { setTimeout(function(){ window.print(); }, 300); }</script>
</body></html>`;

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const tipLabel = (t: StarterType) =>
    t === "razeni" ? "Raženi starter" : t === "biga" ? "Biga" : "Starter";

  /** Tablica jednog stupnja: starter koji ulazi + brašno + voda. */
  const renderStupanj = (
    stupanj: StarterStupanj,
    starterNaziv: string,
    brasnoNaziv: string,
    ukupnoNaziv: string,
    poZamjesu?: StarterStupanj
  ) => (
    <div className="mes-table-wrap">
      <table className="mes-table">
        <thead>
          <tr>
            <th>Sirovina</th>
            <th>{poZamjesu ? "Ukupno [kg]" : "Zamjes [kg]"}</th>
            {poZamjesu && <th>Po zamjesu [kg]</th>}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{starterNaziv}</td>
            <td>{fmt(stupanj.starter)}</td>
            {poZamjesu && <td>{fmt(poZamjesu.starter)}</td>}
          </tr>
          <tr>
            <td>{brasnoNaziv}</td>
            <td>{fmt(stupanj.brasno)}</td>
            {poZamjesu && <td>{fmt(poZamjesu.brasno)}</td>}
          </tr>
          <tr>
            <td>VODA</td>
            <td>{fmt(stupanj.voda)}</td>
            {poZamjesu && <td>{fmt(poZamjesu.voda)}</td>}
          </tr>
          <tr className="total-row">
            <td>{ukupnoNaziv}</td>
            <td>{fmt(stupanj.ukupno)}</td>
            {poZamjesu && <td>{fmt(poZamjesu.ukupno)}</td>}
          </tr>
        </tbody>
      </table>
    </div>
  );

  const renderParamPair = (
    naslov: string,
    omjerField: keyof ParamForm,
    omjerLabel: string,
    hidrField: keyof ParamForm
  ) => (
    <div className="starter-param-row">
      <div className="starter-param-title">{naslov}</div>
      <div className="form-group">
        <label>{omjerLabel}</label>
        <input
          type="number"
          step="0.01"
          min="0"
          className="input-classic"
          value={paramForm[omjerField]}
          onChange={(e) => handleParamChange(omjerField, e.target.value)}
        />
      </div>
      <div className="form-group">
        <label>Hidratacija (% na brašno)</label>
        <input
          type="number"
          step="1"
          min="0"
          className="input-classic"
          value={paramForm[hidrField]}
          onChange={(e) => handleParamChange(hidrField, e.target.value)}
        />
      </div>
    </div>
  );

  return (
    <div className="mes-page">
      <Header title="MES - Priprema startera" />
      <div className="main-content-flex">
        <Tabs data={mes_tab_data} />
        <div className="section mes-content">
          <MesDateSelector
            selectedDate={selectedDate}
            onDateChange={handleDateChange}
            showTimelineIndicators={true}
          />

          {loading ? (
            <div className="mes-section">
              <p>Ucitavanje...</p>
            </div>
          ) : timeline ? (
            <div className={`mes-status-banner ${timeline.Status}`}>
              Timeline za {selectedDate}: <strong>{timeline.Status.toUpperCase()}</strong>
              {timeline.Status !== "confirmed" && (
                <span style={{ marginLeft: "1rem", color: "#f39c12" }}>
                  (Timeline mora biti potvrdjen za izracun startera)
                </span>
              )}
            </div>
          ) : (
            <div className="mes-status-banner draft">Nema timeline-a za {selectedDate}</div>
          )}

          {timeline?.Status === "confirmed" && !hasAny && !calculating && (
            <div className="mes-section">
              <p>Plan za {selectedDate} ne sadrži startere ni bigu.</p>
            </div>
          )}

          {timeline?.Status === "confirmed" && hasAny && (
            <>
              {/* Ulazne količine — iz plana, uz mogućnost ručne korekcije */}
              <div className="mes-section">
                <h3>Ulazne količine</h3>
                <div className="starter-input-grid">
                  <div className="form-group">
                    <label>Starter iz radnog naloga (kg)</label>
                    <input
                      type="number"
                      step="0.1"
                      className="input-classic"
                      value={override.starter}
                      onChange={(e) => setOverride({ ...override, starter: e.target.value })}
                    />
                    <small>Iz plana: {fmt(demand.starter)} kg</small>
                  </div>
                  <div className="form-group">
                    <label>Raženi starter iz radnog naloga (kg)</label>
                    <input
                      type="number"
                      step="0.1"
                      className="input-classic"
                      value={override.razeni}
                      onChange={(e) => setOverride({ ...override, razeni: e.target.value })}
                    />
                    <small>Iz plana: {fmt(demand.razeni)} kg</small>
                  </div>
                  <div className="form-group">
                    <label>Biga iz radnog naloga (kg)</label>
                    <input
                      type="number"
                      step="0.1"
                      className="input-classic"
                      value={override.biga}
                      onChange={(e) => setOverride({ ...override, biga: e.target.value })}
                    />
                    <small>Iz plana: {fmt(demand.biga)} kg</small>
                  </div>
                  <div className="form-group">
                    <label>Sigurnosna količina (kg)</label>
                    <input
                      type="number"
                      step="1"
                      min="0"
                      className="input-classic"
                      value={paramForm.sigurnosnaKg}
                      onChange={(e) => handleParamChange("sigurnosnaKg", e.target.value)}
                    />
                    <small>Dodaje se na jutarnji starter</small>
                  </div>
                  <div className="form-group">
                    <label>Dnevni starter / matica (kg)</label>
                    <input
                      type="number"
                      step="1"
                      min="0"
                      className="input-classic"
                      value={paramForm.dnevnaMaticaKg}
                      onChange={(e) => handleParamChange("dnevnaMaticaKg", e.target.value)}
                    />
                    <small>Ostaje za idući dan</small>
                  </div>
                  <div className="form-group">
                    <label>Mikser za noćni starter</label>
                    <select
                      className="select-classic"
                      value={mixerId ?? ""}
                      onChange={(e) =>
                        setMixerId(e.target.value ? parseInt(e.target.value, 10) : null)
                      }
                    >
                      <option value="">— odaberi mikser —</option>
                      {mixers.map((m) => (
                        <option key={m.ID} value={m.ID}>
                          {m.MachineName}
                          {Number(m.CapacityMaxKg) > 0 ? ` (${fmt(Number(m.CapacityMaxKg), 0)} kg)` : ""}
                        </option>
                      ))}
                    </select>
                    <small>
                      {mixerCapacity > 0
                        ? `Kapacitet ${fmt(mixerCapacity, 0)} kg po zamjesu (Baza → Strojevi)`
                        : `Mikser bez upisanog kapaciteta — računa se s ${fmt(
                            DEFAULT_STARTER_PARAMS.kapacitetMikseraKg,
                            0
                          )} kg`}
                    </small>
                  </div>
                </div>
                <div className="mes-actions" style={{ marginTop: "1rem" }}>
                  <PrimaryButton
                    onSubmit={() => timeline && calculateNeeds(timeline)}
                    loading={calculating}
                  >
                    Preracunaj iz plana
                  </PrimaryButton>
                  <OutlinedButton onClick={() => setShowParams((v) => !v)}>
                    {showParams ? "Sakrij parametre" : "Parametri osvježavanja"}
                  </OutlinedButton>
                </div>
              </div>

              {/* Tehnološki parametri — spremaju se u MES postavke */}
              {showParams && (
                <div className="mes-section">
                  <h3>Parametri osvježavanja</h3>
                  <p className="starter-hint">
                    Svaki stupanj je jedno osvježavanje: na 1 dio startera dolazi zadani
                    dio brašna, a voda je postotak na to brašno. Vrijednosti se spremaju u
                    MES postavke i vrijede za sve dane.
                  </p>
                  {renderParamPair(
                    "Noćni starter",
                    "omjerBrasnoNocni",
                    "Brašna na 1 dio matice",
                    "hidratacijaNocni"
                  )}
                  {renderParamPair(
                    "Jutarnji starter",
                    "omjerBrasnoJutro",
                    "Brašna na 1 dio noćnog",
                    "hidratacijaJutro"
                  )}
                  {renderParamPair(
                    "Raženi starter",
                    "omjerBrasnoRazeni",
                    "Raženog brašna na 1 dio noćnog",
                    "hidratacijaRazeni"
                  )}
                  {renderParamPair(
                    "Dnevni starter / matica",
                    "omjerBrasnoDnevni",
                    "Brašna na 1 dio jutarnjeg",
                    "hidratacijaDnevni"
                  )}
                  <div className="mes-actions" style={{ marginTop: "1rem" }}>
                    <PrimaryButton onSubmit={handleSaveParams} loading={savingParams}>
                      Spremi parametre
                    </PrimaryButton>
                    <OutlinedButton onClick={handleResetParams}>
                      Vrati zadane vrijednosti
                    </OutlinedButton>
                  </div>
                </div>
              )}

              {/* 1. NOĆNI STARTER */}
              {plan.nocni.ukupno > 0 && (
                <div className="mes-section">
                  <h3>
                    1. Noćni starter — {fmt(plan.nocni.ukupno)} kg{" "}
                    <small>
                      ({plan.nocni.nZamjesa} zamjesa
                      {selectedMixer ? ` · ${selectedMixer.MachineName}` : ""}, max{" "}
                      {fmt(params.kapacitetMikseraKg, 0)} kg)
                    </small>
                  </h3>
                  <p className="starter-hint">
                    Priprema dan prije, ~12h | omjer 1 (matica) : {fmt(params.omjerBrasnoNocni, 2)}{" "}
                    (brašno) uz {pct(params.hidratacijaNocni)} hidratacije
                  </p>
                  {renderStupanj(
                    plan.nocni,
                    "STARTER DNEVNI/MATICA",
                    "MANITOBA BRAŠNO",
                    "UKUPNO NOĆNI",
                    plan.nocni.poZamjesu
                  )}
                  <p className="starter-hint">
                    Od toga za jutarnji starter {fmt(plan.jutro.starter)} kg
                    {plan.razeni.ukupno > 0
                      ? `, za raženi starter ${fmt(plan.razeni.starter)} kg`
                      : ""}
                    .
                  </p>
                </div>
              )}

              {/* 2. JUTARNJI STARTER */}
              {plan.jutro.ukupno > 0 && (
                <div className="mes-section">
                  <h3>2. Jutarnji starter — {fmt(plan.jutro.ukupno)} kg</h3>
                  <p className="starter-hint">
                    Radni nalog {fmt(starterUkupno)} kg + sigurnosna {fmt(params.sigurnosnaKg)} kg |
                    omjer 1 (noćni) : {fmt(params.omjerBrasnoJutro, 2)} (brašno) uz{" "}
                    {pct(params.hidratacijaJutro)} hidratacije
                  </p>
                  {renderStupanj(
                    plan.jutro,
                    "STARTER NOĆNI",
                    "MANITOBA BRAŠNO",
                    "UKUPNO JUTARNJI"
                  )}
                </div>
              )}

              {/* 3. RAŽENI STARTER */}
              {plan.razeni.ukupno > 0 && (
                <div className="mes-section">
                  <h3>3. Raženi starter — {fmt(plan.razeni.ukupno)} kg</h3>
                  <p className="starter-hint">
                    Omjer 1 (noćni) : {fmt(params.omjerBrasnoRazeni, 2)} (raženo brašno) uz{" "}
                    {pct(params.hidratacijaRazeni)} hidratacije
                  </p>
                  {renderStupanj(plan.razeni, "STARTER NOĆNI", "RAŽENO BRAŠNO", "UKUPNO RAŽENI")}
                </div>
              )}

              {/* 4. DNEVNI STARTER / MATICA */}
              {plan.dnevni.ukupno > 0 && (
                <div className="mes-section">
                  <h3>4. Dnevni starter / matica — {fmt(plan.dnevni.ukupno)} kg</h3>
                  <p className="starter-hint">
                    Ostaje za idući dan | omjer 1 (jutarnji) : {fmt(params.omjerBrasnoDnevni, 2)}{" "}
                    (brašno) uz {pct(params.hidratacijaDnevni)} hidratacije
                  </p>
                  {renderStupanj(
                    plan.dnevni,
                    "STARTER JUTRO",
                    "MANITOBA BRAŠNO",
                    "UKUPNO DNEVNI/MATICA"
                  )}
                  <p className="starter-hint">
                    Troši {fmt(plan.dnevni.starter)} kg jutarnjeg startera — pokriveno
                    sigurnosnom količinom od {fmt(params.sigurnosnaKg)} kg.
                  </p>
                </div>
              )}

              {/* 5. BIGA */}
              {bigaUkupno > 0 && (
                <div className="mes-section">
                  <h3>5. Biga priprema — {fmt(bigaUkupno)} kg</h3>
                  <p className="starter-hint">
                    Priprema dan prije | smrznuti kvasac 0.05%, voda 38.63%
                  </p>
                  <div className="mes-table-wrap">
                    <table className="mes-table" style={{ maxWidth: 480 }}>
                      <thead>
                        <tr>
                          <th>Sirovina</th>
                          <th>Za 1 kg</th>
                          <th>Zamjes [kg]</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>MANITOBA BRAŠNO</td>
                          <td>0.6132</td>
                          <td>{fmt(bg.brasno)}</td>
                        </tr>
                        <tr>
                          <td>SMRZNUTI KVASAC</td>
                          <td>0.0005</td>
                          <td>{fmt(bg.kvasac, 4)}</td>
                        </tr>
                        <tr>
                          <td>VODA</td>
                          <td>0.3863</td>
                          <td>{fmt(bg.voda)}</td>
                        </tr>
                        <tr className="total-row">
                          <td>UKUPNO BIGA</td>
                          <td></td>
                          <td>{fmt(bigaUkupno)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Detalji potražnje po zamjesu */}
              {details.length > 0 && (
                <div className="mes-section">
                  <h3>Detalji potražnje po zamjesu</h3>
                  <div className="mes-table-wrap">
                    <table className="mes-table">
                      <thead>
                        <tr>
                          <th>ID Zamjesa</th>
                          <th>Proizvod</th>
                          <th>Masa (kg)</th>
                          <th>Tip</th>
                          <th>Potrebno (kg)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {details.map((d, idx) => (
                          <tr key={idx}>
                            <td>
                              <code className="batch-id-code">{d.id_zamjesa}</code>
                            </td>
                            <td>{d.product_name}</td>
                            <td>{d.finalna_masa_kg.toFixed(2)}</td>
                            <td>
                              <span className="mixer-badge">{tipLabel(d.tip)}</span>
                            </td>
                            <td>{d.potrebno_kg.toFixed(3)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Print */}
              <div className="mes-section">
                <div className="mes-actions">
                  <PrimaryButton onSubmit={handlePrint} loading={false}>
                    Pripremi za print
                  </PrimaryButton>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        .starter-input-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(200px, 1fr));
          gap: 1rem;
        }
        .starter-input-grid small { color: #888; }
        .starter-hint {
          color: #888;
          font-size: 0.85rem;
          margin: 0 0 0.5rem;
        }
        .starter-param-row {
          display: grid;
          grid-template-columns: minmax(180px, 1fr) minmax(200px, 1fr) minmax(200px, 1fr);
          gap: 1rem;
          align-items: end;
          margin-bottom: 0.75rem;
        }
        .starter-param-title { font-weight: 600; padding-bottom: 0.6rem; }
        @media (max-width: 900px) {
          .starter-input-grid,
          .starter-param-row { grid-template-columns: 1fr; }
        }
      `}</style>

      <ToastContainer autoClose={5000} position="bottom-right" hideProgressBar={false} theme="colored" />
    </div>
  );
};

export default StarterPlan;
